import { NavLink, Navigate, Outlet, Link, useNavigate } from "react-router-dom";
import { Home, Compass, Heart, Layers, MessageCircle, Wallet, UserRound, Radio, Bookmark, Shield, BarChart3, LogOut, Search, Coins, Bell, ChevronDown, ExternalLink } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { LangSwitch } from "../components/Header";
import { DEMO_FAN } from "../constants/demoData";

const fmt = (n) => n.toLocaleString("es-CO");

const NAV = [
  ["home", "/app", Home, true],
  ["discover", "/app/discover", Compass],
  ["following", "/app/following", Heart],
  ["collections", "/app/collections", Layers],
  ["messages", "/app/messages", MessageCircle],
  ["wallet", "/app/wallet", Wallet],
  ["profile", "/app/profile", UserRound],
];
const NAV2 = [
  ["live", "/app/live", Radio],
  ["library", "/app/collections", Bookmark],
  ["security", "/app/security", Shield],
  ["studio", "/app/studio", BarChart3],
];

export const Avatar = ({ name = "DR", size = "h-9 w-9", className = "" }) => (
  <span className={`${size} inline-flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500 font-display text-xs font-extrabold text-white ${className}`}>{name}</span>
);

export default function DemoLayout() {
  const { t } = useLang();
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const d = t.demo;

  if (checking) return <div className="flex min-h-screen items-center justify-center text-sm text-mist">…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const item = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-violet-100 text-violet-800" : "text-ink-2 hover:bg-lilac hover:text-ink"}`;

  return (
    <div data-testid="demo-app" className="min-h-screen bg-white">
      <div data-testid="demo-banner" className="bg-ink px-4 py-2 text-center text-xs text-violet-100">
        <span className="mr-2 rounded-full bg-pink-500 px-2 py-0.5 font-mono text-[0.6rem] font-semibold tracking-widest text-white">{d.ribbon}</span>
        {d.banner}
      </div>

      <header className="sticky top-0 z-40 border-b border-line bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-4 sm:px-6">
          <Link to="/app" data-testid="demo-brand" className="flex items-baseline gap-2">
            <span className="font-display text-2xl font-extrabold tracking-tight text-ink">GOOX<span className="ml-0.5 inline-block h-2 w-2 rounded-full bg-gradient-to-br from-violet-500 to-pink-500" /></span>
            <span className="eyebrow hidden text-[0.58rem] text-mist md:inline">Experience Engine</span>
          </Link>
          <nav className="ml-4 hidden items-center gap-1 lg:flex">
            {NAV.slice(0, 4).map(([k, to, , end]) => (
              <NavLink key={k} to={to} end={end} data-testid={`demo-top-${k}`} className={({ isActive }) => `pill px-3.5 py-1.5 text-sm font-medium ${isActive ? "bg-violet-100 text-violet-800" : "text-ink-2 hover:bg-lilac"}`}>{d.nav[k]}</NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <label className="hidden items-center gap-2 rounded-full border border-line bg-canvas px-3 py-1.5 md:flex">
              <Search size={14} className="text-mist" />
              <input data-testid="demo-search-input" placeholder={d.search} className="w-44 bg-transparent text-xs outline-none placeholder:text-mist" />
            </label>
            <button data-testid="demo-token-balance" onClick={() => navigate("/app/wallet")} className="pill inline-flex items-center gap-1.5 border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-violet-300">
              <Coins size={14} className="text-amber-500" /> {fmt(DEMO_FAN.tokens)} <span className="text-violet-600">+</span>
            </button>
            <button className="relative rounded-full p-2 text-ink-2 hover:bg-lilac" aria-label="Notifications"><Bell size={16} /><span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-pink-500" /></button>
            <LangSwitch compact />
            <div className="group relative">
              <button data-testid="demo-user-menu" className="flex items-center gap-1.5 rounded-full border border-line bg-white p-1 pr-2">
                <Avatar size="h-7 w-7" /><ChevronDown size={14} className="text-mist" />
              </button>
              <div className="invisible absolute right-0 top-full mt-2 w-60 rounded-2xl border border-line bg-white p-2 opacity-0 shadow-xl transition-all group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-ink">{user.display_name}</p>
                  <p className="text-xs text-mist">@{user.username} · {d.profile.demoRole}</p>
                </div>
                <Link to="/app/security" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-ink-2 hover:bg-lilac"><Shield size={14} /> {d.nav.security}</Link>
                <Link to="/" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-ink-2 hover:bg-lilac"><ExternalLink size={14} /> {d.nav.backToSite}</Link>
                <button data-testid="demo-logout-button" onClick={() => { logout(); navigate("/login"); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"><LogOut size={14} /> {d.nav.signOut}</button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1400px] gap-8 px-4 py-6 sm:px-6 lg:grid-cols-[220px_1fr]">
        <aside className="hidden lg:block">
          <nav className="sticky top-24 flex flex-col gap-1">
            {NAV.map(([k, to, Icon, end]) => (
              <NavLink key={k} to={to} end={end} data-testid={`demo-nav-${k}`} className={item}><Icon size={17} /> {d.nav[k]}</NavLink>
            ))}
            <div className="my-3 border-t border-line" />
            {NAV2.map(([k, to, Icon]) => (
              <NavLink key={k} to={to} data-testid={`demo-nav-${k}`} className={item}><Icon size={17} /> {d.nav[k]}</NavLink>
            ))}
            <p className="mt-10 px-3 text-[0.65rem] leading-relaxed text-mist">18+ · {t.footer.operatedBy} CAP GROUP S.A.S.<br /><Link to="/legal/terms" className="underline">{t.legalTitles.terms}</Link> · <Link to="/legal/privacy" className="underline">{t.legalTitles.privacy}</Link></p>
          </nav>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex justify-around border-t border-line bg-white/95 py-2 backdrop-blur lg:hidden">
        {NAV.slice(0, 5).map(([k, to, Icon, end]) => (
          <NavLink key={k} to={to} end={end} className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 text-[0.6rem] ${isActive ? "text-violet-700" : "text-mist"}`}><Icon size={18} />{d.nav[k]}</NavLink>
        ))}
      </nav>
    </div>
  );
}
